import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { MapPin, CalendarBlank, Clock, Lock, LockOpen, Sparkle } from "@phosphor-icons/react";

import api from "@/lib/api";
import { PageShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Code wall
  const [code, setCode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [validating, setValidating] = useState(false);
  const [codeLabel, setCodeLabel] = useState("");

  // Purchase form
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [attendeeType, setAttendeeType] = useState("couple");
  const [quantity, setQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    api
      .get(`/events/${id}`)
      .then((r) => setEvent(r.data))
      .catch(() => {
        toast.error("Event not found");
        navigate("/social-events");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleValidate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/codes/validate", { code: code.trim() });
      setCodeLabel(data.label || "");
      setUnlocked(true);
      toast.success("Access granted");
    } catch (err) {
      const detail = err.response?.data?.detail || "Invalid code";
      toast.error(typeof detail === "string" ? detail : "Invalid code");
    } finally {
      setValidating(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error("Please complete name and email");
      return;
    }
    setPurchasing(true);
    try {
      // Simulate payment processor delay
      await new Promise((r) => setTimeout(r, 1100));
      const { data } = await api.post("/tickets/purchase", {
        event_id: id,
        code: code.trim(),
        full_name: fullName.trim(),
        email: email.trim(),
        attendee_type: attendeeType,
        quantity: Number(quantity),
      });
      toast.success("Ticket reserved");
      navigate(`/ticket/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Purchase failed";
      toast.error(typeof detail === "string" ? detail : "Purchase failed");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="p-16 text-[#A3A3A3] font-mono-overline">Loading…</div>
      </PageShell>
    );
  }
  if (!event) return null;

  const dt = typeof event.date_time === "string" ? parseISO(event.date_time) : new Date(event.date_time);
  const unitPrice = attendeeType === "couple" ? event.price_couple : event.price_single_lady;
  const total = (unitPrice * Number(quantity || 1)).toFixed(2);

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden" data-testid="event-hero">
        <img src={event.image_url} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/20" />
        <div className="absolute bottom-0 left-0 right-0 px-6 lg:px-12 pb-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="font-mono-overline text-[#e2a9f1]">{event.city}</div>
              {event.event_type === "spicy" && (
                <span
                  data-testid="event-detail-spicy-badge"
                  className="font-mono-overline text-[10px] text-[#0A0A0A] bg-[#e2a9f1] px-2 py-1"
                >
                  Spicy · Play Permitted
                </span>
              )}
            </div>
            <h1
              className="text-5xl md:text-7xl font-serif-display text-[#EDEDED] leading-[0.95] max-w-3xl"
              data-testid="event-title"
            >
              {event.title}
            </h1>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 py-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          {/* Details */}
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-3 gap-6 mb-10 pb-10 border-b border-[#2A2A2A]" data-testid="event-meta">
              <div>
                <CalendarBlank size={20} weight="thin" className="text-[#e2a9f1] mb-3" />
                <div className="font-mono-overline text-[#A3A3A3] mb-1">Date</div>
                <div className="text-[#EDEDED]">{format(dt, "EEE d MMM yyyy")}</div>
              </div>
              <div>
                <Clock size={20} weight="thin" className="text-[#e2a9f1] mb-3" />
                <div className="font-mono-overline text-[#A3A3A3] mb-1">Doors</div>
                <div className="text-[#EDEDED]">{format(dt, "h:mm a")}</div>
              </div>
              <div>
                <MapPin size={20} weight="thin" className="text-[#e2a9f1] mb-3" />
                <div className="font-mono-overline text-[#A3A3A3] mb-1">Venue</div>
                <div className="text-[#EDEDED]">{event.venue}</div>
              </div>
            </div>

            <div className="font-mono-overline text-[#e2a9f1] mb-4">About the evening</div>
            <p className="text-lg text-[#EDEDED]/85 leading-relaxed mb-8 whitespace-pre-line" data-testid="event-description">
              {event.description}
            </p>

            {event.dress_code && (
              <div className="border border-[#2A2A2A] p-6 inline-block">
                <div className="font-mono-overline text-[#A3A3A3] mb-2">Dress Code</div>
                <div className="font-serif-display text-xl text-[#e2a9f1]">{event.dress_code}</div>
              </div>
            )}
          </div>

          {/* Code Wall + Purchase */}
          <aside className="lg:col-span-5">
            <div className="sticky top-28 bg-[#111111] border border-[#2A2A2A] p-8 md:p-10 relative overflow-hidden" data-testid="purchase-panel">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-mono-overline text-[#e2a9f1]">Reservation</div>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-baseline gap-3">
                      <span className="text-3xl font-serif-display text-[#EDEDED]">£{Number(event.price_couple).toFixed(0)}</span>
                      <span className="text-sm text-[#A3A3A3]">per couple</span>
                    </div>
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl font-serif-display text-[#EDEDED]">£{Number(event.price_single_lady).toFixed(0)}</span>
                      <span className="text-sm text-[#A3A3A3]">per single lady</span>
                    </div>
                  </div>
                </div>
                {unlocked ? (
                  <LockOpen size={28} weight="thin" className="text-[#e2a9f1]" data-testid="lock-open-icon" />
                ) : (
                  <Lock size={28} weight="thin" className="text-[#A3A3A3]" data-testid="lock-icon" />
                )}
              </div>

              <AnimatePresence mode="wait">
                {!unlocked ? (
                  <motion.form
                    key="code-wall"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    onSubmit={handleValidate}
                    className="pt-6"
                    data-testid="code-wall-form"
                  >
                    <div className="text-center mb-8">
                      <div className="font-mono-overline text-[#A3A3A3] mb-3">Access code required</div>
                      <p className="text-sm text-[#A3A3A3] leading-relaxed max-w-xs mx-auto">
                        Ticketing is gated. Enter the code you received from us or your referrer.
                      </p>
                    </div>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="ACCESS CODE"
                      data-testid="code-wall-input"
                      className="w-full text-center text-2xl md:text-3xl tracking-[0.3em] bg-transparent border-0 border-b-2 border-[#2A2A2A] focus:border-[#e2a9f1] focus:outline-none rounded-none py-4 text-[#EDEDED] placeholder:text-[#2A2A2A] uppercase"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={validating || !code.trim()}
                      data-testid="code-wall-submit"
                      className="w-full mt-8 bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {validating ? "Verifying…" : "Unlock Tickets"}
                    </button>
                    <div className="text-center mt-6 font-mono-overline text-[#A3A3A3]">
                      Don&rsquo;t have a code? Email <span className="text-[#e2a9f1]">access@swingleevents.com</span>
                    </div>
                  </motion.form>
                ) : (
                  <motion.form
                    key="purchase"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    onSubmit={handlePurchase}
                    className="pt-2 space-y-5"
                    data-testid="purchase-form"
                  >
                    {codeLabel && (
                      <div className="flex items-center gap-2 font-mono-overline text-[#e2a9f1] -mt-2">
                        <Sparkle size={14} weight="thin" /> {codeLabel}
                      </div>
                    )}
                    <div>
                      <Label htmlFor="full-name" className="font-mono-overline text-[#A3A3A3]">Full Name</Label>
                      <Input
                        id="full-name"
                        data-testid="purchase-name-input"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="mt-2 bg-[#0A0A0A] border-[#2A2A2A] focus-visible:ring-[#e2a9f1] focus-visible:border-[#e2a9f1] text-[#EDEDED] rounded-sm h-11"
                        placeholder="Alex & Sam Morgan"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-mono-overline text-[#A3A3A3]">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="purchase-email-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 bg-[#0A0A0A] border-[#2A2A2A] focus-visible:ring-[#e2a9f1] focus-visible:border-[#e2a9f1] text-[#EDEDED] rounded-sm h-11"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono-overline text-[#A3A3A3]">Attending as</Label>
                        <Select value={attendeeType} onValueChange={setAttendeeType}>
                          <SelectTrigger
                            data-testid="purchase-attendee-type"
                            className="mt-2 bg-[#0A0A0A] border-[#2A2A2A] focus:ring-[#e2a9f1] text-[#EDEDED] rounded-sm h-11"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111111] border-[#2A2A2A] text-[#EDEDED]">
                            <SelectItem value="couple">Couple — £{Number(event.price_couple).toFixed(0)}</SelectItem>
                            <SelectItem value="single_lady">Single Lady — £{Number(event.price_single_lady).toFixed(0)}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="qty" className="font-mono-overline text-[#A3A3A3]">Tickets</Label>
                        <Input
                          id="qty"
                          type="number"
                          min={1}
                          max={10}
                          data-testid="purchase-quantity-input"
                          value={quantity}
                          onChange={(e) => setQuantity(e.target.value)}
                          className="mt-2 bg-[#0A0A0A] border-[#2A2A2A] focus-visible:ring-[#e2a9f1] focus-visible:border-[#e2a9f1] text-[#EDEDED] rounded-sm h-11"
                        />
                      </div>
                    </div>

                    <div className="border-t border-[#2A2A2A] pt-5 flex justify-between items-center">
                      <span className="font-mono-overline text-[#A3A3A3]">Total</span>
                      <span className="text-2xl font-serif-display text-[#e2a9f1]" data-testid="purchase-total">
                        £{total}
                      </span>
                    </div>

                    <button
                      type="submit"
                      disabled={purchasing}
                      data-testid="purchase-submit-btn"
                      className="w-full bg-[#e2a9f1] text-[#0A0A0A] font-semibold tracking-wide hover:bg-[#efc8f7] transition-colors duration-300 rounded-sm py-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {purchasing ? "Securing your evening…" : "Pay & Reserve"}
                    </button>
                    <p className="text-xs text-[#A3A3A3] text-center leading-relaxed">
                      Payment is simulated for demo. Your card statement will read &ldquo;SW Events.&rdquo; Tickets
                      are non-refundable within 7 days of the event.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}
